import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { CgProfile } from "react-icons/cg";
import { IoSettingsOutline } from "react-icons/io5";
import { LiaKeySolid, LiaHomeSolid } from "react-icons/lia";
import { HiLink } from "react-icons/hi2";
import { BsArrowCounterclockwise } from "react-icons/bs";
import { FiLogOut } from "react-icons/fi";
import { MdOutlineLogout, MdOutlineAdminPanelSettings } from "react-icons/md";
import { IoMdClose } from "react-icons/io";
import { userLogout } from "../Redux/Actions/AuthAction";
import { motion } from "framer-motion";

const Navbar = ({ profile }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const UsersProfile = useSelector((state) => state.UsersProfile || {});

  const [profileData, setProfileData] = useState(null);
  //  const imageUrl = profileData.images.length > 0 ? profileData.images[0] : "default-image-url.jpg";


  // console.log(profileData)
  const [isOpen, setIsOpen] = useState(false);
  const [confirmLogout, setConfirmLogout] = useState(false);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 768);
  const menuRef = useRef();


  useEffect(() => {
    if (UsersProfile?.personalDetails) {
      setProfileData(UsersProfile.personalDetails.data);
    }
  }, [UsersProfile]);

  const fullName = useMemo(() => {
    return profileData
      ? `${profileData.firstname || ""} ${profileData.lastname || ""}`
      : "";
  }, [profileData]);

  // Check if user is admin
  const isAdmin = useMemo(() => {
    return !!profileData?.is_admin;
  }, [profileData]);

  // Check if user is sub-admin
  const isSubAdmin = useMemo(() => {
    return profileData?.role === "sub_admin";
  }, [profileData]);

  // Handle screen size change
  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth >= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const handleLogout = useCallback(() => {
    dispatch(userLogout(navigate));
    setConfirmLogout(false);
    navigate("/login");
  }, [dispatch, navigate]);

  const isActive = (path) => location.pathname === path;

  // Regular user menu items (hidden for admin)
  const regularUserMenuItems = [
    { label: "My Profile", path: "/profile/My-Profile", icon: <CgProfile size={20} /> },
    { label: "Edit Profile", path: "/profile/Edit-Profile", icon: <IoSettingsOutline size={20} /> },
    { label: "Change Password", path: "/profile/Change-Password", icon: <LiaKeySolid size={20} /> },
    { label: "Payment History", path: "/profile/Payment-History", icon: <BsArrowCounterclockwise size={20} /> },
  ];

  // Admin menu items (full admin only)
  const adminMenuItems = [
    { label: "Admin Profile", path: "/admin/profile", icon: <CgProfile size={20} /> },
    { label: "Admin Management", path: "/admin", icon: <IoSettingsOutline size={20} /> },
    { label: "Sub-Admin Management", path: "/admin/sub-admins", icon: <MdOutlineAdminPanelSettings size={20} /> },
    { label: "Change Password", path: "/profile/Change-Password", icon: <LiaKeySolid size={20} /> },
  ];

  // Sub-admin menu items
  const subAdminMenuItems = [
    { label: "Admin Profile", path: "/admin/profile", icon: <CgProfile size={20} /> },
    { label: "Admin Management", path: "/admin", icon: <IoSettingsOutline size={20} /> },
    { label: "Change Password", path: "/profile/Change-Password", icon: <LiaKeySolid size={20} /> },
  ];

  // Desktop menu items - conditionally show based on user role
  const menuItemsDesktop = isAdmin ? adminMenuItems : isSubAdmin ? subAdminMenuItems : regularUserMenuItems;

  // Mobile menu items
  const menuItemsMobile = [
    { label: "Home", path: "/", icon: <LiaHomeSolid size={20} /> },
    ...(isAdmin ? adminMenuItems : isSubAdmin ? subAdminMenuItems : regularUserMenuItems),
    { label: "Profiles Contacted", path: "/profile/Contacted", icon: <HiLink size={20} /> },
  ];

  const renderMenuItems = (items) =>
    items.map(({ label, path, icon }) => (
      <li
        key={label}
        className="px-4 py-2 hover:bg-gray-100 cursor-pointer font-semibold flex items-center gap-3 group"
        onClick={() => {
          navigate(path);
          setIsOpen(false);
        }}
      >
        <div className="flex items-center gap-3 font-semibold">
          <span className="transition-transform duration-300 ease-in-out group-hover:translate-x-2">
            {icon}
          </span>
          <span className="transition-transform duration-300 ease-in-out group-hover:translate-x-2">
            {label}
          </span>
        </div>
      </li>
    ));

  return (
    <div className="h-24 bg-[#151515] relative z-10">
      <div className="max-w-screen-xl mx-auto px-4 md:px-16 h-full">
        <div className="flex py-7 justify-between h-full gap-3 items-center">
          {/* Logo */}
          <img src="/images/logo.png" alt="Logo" />

          {/* Desktop Nav */}
          <div className="hidden md:flex gap-6 text-base font-semibold">
            {[
              { label: "Home", path: "/" },
              ...(isAdmin || isSubAdmin ? [{ label: "Admin Management", path: "/admin" }] : [])
            ].map((item) => (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className={`nav-link relative text-white ${isActive(item.path) ? "active-tab" : ""
                  }`}
              >
                {item.label}
              </button>
            ))}
          </div>


          {/* Mobile/Desktop Dropdown */}
          <div className="flex gap-3">
            <div className="text-base font-semibold text-white flex justify-center items-center hidden sm:flex">
              {fullName}
              {/* {isAdmin && <span className="ml-2 text-xs bg-red-500 text-white px-2 py-1 rounded">Admin</span>} */}
            </div>
            <div className="relative" ref={menuRef}>
              <motion.img
                src={profileData?.images?.[0] || "/images/Navbar_icon-2.png"}
                className="cursor-pointer border border-white rounded-full bg-white/10 ring-1 ring-white/10 h-10 w-[39px] object-cover"
                onClick={() => setIsOpen(!isOpen)}
                alt="Menu Icon"
                animate={{
                  scale: isOpen ? 1.03 : 1
                }}
                whileHover={{
                  scale: 1.03
                }}
                whileTap={{
                  scale: 0.95
                }}
                transition={{
                  type: "spring",
                  stiffness: 400,
                  damping: 15
                }}
              />

              {isOpen && (
                <div className="absolute right-0 mt-2 w-[220px] bg-white shadow-lg rounded-lg z-50">
                  <ul className="py-2">
                    {renderMenuItems(isDesktop ? menuItemsDesktop : menuItemsMobile)}
                    <li
                      className="px-4 py-2 hover:bg-gray-100 cursor-pointer font-semibold flex items-center gap-3 text-red-600 group"
                      onClick={() => {
                        setConfirmLogout(true);
                        setIsOpen(false);
                      }}
                    >
                      <div className="flex items-center gap-3 font-semibold">
                        <span className="transition-transform duration-300 ease-in-out group-hover:translate-x-2">
                          <FiLogOut size={20} />
                        </span>
                        <span className="transition-transform duration-300 ease-in-out group-hover:translate-x-2">
                          Log Out
                        </span>
                      </div>
                    </li>
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Logout Confirmation Modal */}
      {confirmLogout && (
        <div className="fixed inset-0 flex items-center justify-center bg-gray-800 bg-opacity-50 p-4 sm:p-0 z-50" onClick={() => setConfirmLogout(false)}>
          <div className="bg-white p-6 rounded-3xl shadow-lg max-w-xs w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-end" >
              <IoMdClose size={20} className="cursor-pointer" onClick={() => setConfirmLogout(false)} />
            </div>
            <div className="flex justify-center mt-3">
              <div className="border-2 border-[#E72848] rounded-full p-4">
                <MdOutlineLogout size={30} color="#E72848" />
              </div>
            </div>
            <div className="px-4 py-8 flex flex-col items-center text-center">
              <div className="text-xl font-bold">Are you sure?</div>
              <div>Do you really want to Logout?</div>
            </div>
            <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-5">
              <button
                className="px-6 py-2 border border-gray-300 rounded-xl w-full sm:w-auto"
                onClick={() => setConfirmLogout(false)}
              >
                Cancel
              </button>
              <button
                className="px-6 py-2 bg-[#E72848] text-white rounded-xl w-full sm:w-auto"
                onClick={handleLogout}
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Navbar;